import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "../../../../common/utils/object-utils";
import { CreateDestineringsomradeCommand, UpdateDestineringsomradeCommand } from "../commands/commands";
import { DsoEvents } from "../models/dso-events";
import { Destineringsomrade, DestineringsomradeBasdataDto, DestineringsomradeNamnDTO, SearchQuery } from "../models/dso-models";
import { DsoEventService } from "../services/dso-event.service";
import { DsoService } from "../services/dso.service";

@Injectable({
    providedIn: "root"
})
export class DsoCacheService {
    basdata: DestineringsomradeBasdataDto;
    current = new BehaviorSubject<Destineringsomrade>(null);
    initiated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly commandService = inject(CommandService);
    private readonly dsoService = inject(DsoService);
    private readonly eventService = inject(DsoEventService);

    private omraden: Destineringsomrade[] = [];
    private original: Destineringsomrade[] = [];
    private protectedNames: DestineringsomradeNamnDTO[];

    clear() {
        this.omraden.splice(0);
        this.original.splice(0);
    }

    create(wkt: string): Destineringsomrade {
        const omrade = {
            id: null,
            isNew: true,
            namn: "",
            color: this.dsoService.getRandomColor(),
            aktiv: true,
            geometri: wkt,
            guid: GuidUtils.newGuid(),
            handelssortiment: [],
            mottagningsplatser: []
        } as Destineringsomrade;
        this.omraden.push(omrade);
        return omrade;
    }

    delete(guids: string[]) {
        const names = this.omraden.filter(x => guids.some(g => g === x.guid)).map(g => g.namn);
        _.remove(this.protectedNames, n => names.some(d => d === n.namn));
        _.remove(this.omraden, o => guids.some(deleted => deleted === o.guid));
    }

    get(guid: string = null): Destineringsomrade {
        if (ObjectUtils.isNullOrUndefined(guid)) {
            return null;
        }
        return this.omraden.find(o => o.guid.toLowerCase() === guid?.toLowerCase());
    }

    getAll(): Destineringsomrade[] {
        return this.omraden;
    }

    getMany(guids: string[]): Destineringsomrade[] {
        return this.omraden.filter(x => guids.some(y => y === x.guid));
    }

    getSelected(): Destineringsomrade {
        return this.current.value;
    }

    getUniqueName(name: string) {
        const sameish = this.basdata?.destineringsomradeNamn?.filter(n => _.startsWith(n.namn, name)) ?? [];
        return sameish.length > 0 ? this.getUniqueName(`${name} ${sameish.length + 1}`) : name;
    }

    init() {
        this.dsoService.getBasdata().subscribe(basdata => {
            this.basdata = basdata;
            this.protectedNames = ObjectUtils.cloneArray(basdata.destineringsomradeNamn);
            this.initiated.next(true);
        });
    }

    nameExists(name: string, guid: string = null): boolean {
        const found = this.protectedNames?.find(p => p.namn.trim().toLowerCase() === name?.trim().toLowerCase());
        const isMyself = guid === found?.guid;
        return found != null && !isMyself;
    }

    rollback(guid: string): Destineringsomrade {
        this.omraden = ObjectUtils.cloneArray(this.original, true);
        const omrade = this.get(guid);
        if (!ObjectUtils.isNullOrUndefined(omrade)) {
            this.addProtectedName(omrade);
        }
        return omrade;
    }

    save(destineringsomrade: Destineringsomrade, ok: (state: boolean) => void) {
        const command = this.createCommand(destineringsomrade);
        const commandManager = new CommandManager();
        commandManager.updateCommand(command);

        this.commandService.pushCommands(commandManager).then(s => {
            destineringsomrade.isNew = false;
            _.remove(this.original, d => d.guid === destineringsomrade.guid);
            this.original.push(ObjectUtils.deepCopy(destineringsomrade));
            this.addProtectedName(destineringsomrade);
            ok(true);
        });
    }

    search(query: SearchQuery, emit = true) {
        this.dsoService.search(query).subscribe(omraden => {
            this.omraden = omraden;
            this.original = ObjectUtils.cloneArray(omraden, true);
            if (emit) {
                this.eventService.emit(DsoEvents.destineringsomradenLoaded, omraden);
                this.eventService.emit(DsoEvents.dsoSelected, null);
            }
        });
    }

    setSelected(guid: string) {
        const omrade = this.get(guid);
        this.current.next(omrade);
        this.eventService.emit(DsoEvents.dsoSelected, omrade);
    }

    private addProtectedName(destineringsomrade: Destineringsomrade) {
        if (ObjectUtils.isNullOrUndefined(destineringsomrade?.namn) || destineringsomrade.namn === "") {
            return;
        }
        this.protectedNames.push({
            guid: destineringsomrade.guid,
            namn: destineringsomrade.namn,
            id: destineringsomrade.id
        });
    }

    private createCommand(destineringsomrade: Destineringsomrade) {
        if (destineringsomrade?.isNew) {
            return new CreateDestineringsomradeCommand(destineringsomrade);
        } else {
            return new UpdateDestineringsomradeCommand(destineringsomrade);
        }
    }
}
